import React, { Fragment, useState, useEffect } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import PortalMenu from "../Common/PortalMenu";
 import PageTitle from "../../layouts/PageTitle"; 
 import { Row, Col, Card, Table, Dropdown, Modal, Form, Badge } from "react-bootstrap";
import AppPagination from "../Common/AppPagination"; 
 import swal from "sweetalert"; 
 import api from "../../../api"; 
 import usePermission from "../../../hooks/usePermission"; 
 
 const Permissions = () => { 
 const { hasPermission } = usePermission(); 
 const [permissions, setPermissions] = useState([]); 
 const [filteredPermissions, setFilteredPermissions] = useState([]); 
 const [searchQuery, setSearchQuery] = usePersistentState("perm_search", ""); 
 const [permissionModal, setPermissionModal] = useState(false); 
 const [editPermissionModal, setEditPermissionModal] = useState(false); 
 const [editPermission, setEditPermission] = useState(null);
 const [bulkRows, setBulkRows] = useState([{ name: "", description: "" }]);
 
 // Pagination state
 const [currentPage, setCurrentPage] = usePersistentState("perm_page", 1);
 const [itemsPerPage] = useState(15);
 
 const canCreatePermission = hasPermission("create:permissions"); 
 const canEditPermission = hasPermission("edit:permissions"); 
 const canDeletePermission = hasPermission("delete:permissions"); 
 
 useEffect(() => { 
 fetchPermissions(); 
 }, []); 
 
 useEffect(() => { 
 const filtered = permissions.filter(p => 
 p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
 p.description?.toLowerCase().includes(searchQuery.toLowerCase()) 
 ); 
 setFilteredPermissions(filtered); 
 setCurrentPage(1); // Reset to first page on search
 }, [searchQuery, permissions]); 
 
 const fetchPermissions = () => { 
 api.get('/permissions?limit=999') 
 .then(res => { 
 const data = res.data.data || []; 
 setPermissions(data); 
 setFilteredPermissions(data); 
 }) 
 .catch(() => { 
 setPermissions([]); 
 setFilteredPermissions([]); 
 }); 
 }; 
 
 // Pagination logic
 const indexOfLastItem = currentPage * itemsPerPage;
 const indexOfFirstItem = indexOfLastItem - itemsPerPage;
 const currentPermissions = filteredPermissions.slice(indexOfFirstItem, indexOfLastItem);
 const totalPages = Math.ceil(filteredPermissions.length / itemsPerPage);
 
 const paginate = (pageNumber) => setCurrentPage(pageNumber);
 
 const handleBulkRowChange = (index, field, value) => {
 setBulkRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
 };

 const addBulkRow = () => {
 setBulkRows(prev => [...prev, { name: "", description: "" }]);
 };

 const removeBulkRow = (index) => {
 setBulkRows(prev => prev.filter((_, i) => i !== index));
 };

 const handlePermissionSubmit = async (e) => {
 e.preventDefault();

 const validRows = bulkRows.filter(r => r.name.trim());
 if (validRows.length === 0) {
 swal("Error", "Enter at least one permission key", "error");
 return;
 }

 try {
 if (validRows.length === 1) {
 const res = await api.post('/permissions', validRows[0]);
 setPermissions(prev => [...prev, res.data.data]);
 swal("Success", "Permission added successfully!", "success");
 } else {
 const res = await api.post('/permissions/bulk', { permissions: validRows });
 const { created = [], skipped = [], errors = [] } = res.data.data || {};
 setPermissions(prev => [...prev, ...created]);
 let msg = `${created.length} permission(s) created.`;
 if (skipped.length) msg += `\n${skipped.length} skipped (already exist).`;
 if (errors.length) msg += `\n${errors.length} failed.`;
 swal("Done", msg, created.length > 0 ? "success" : "warning");
 }
 setBulkRows([{ name: "", description: "" }]);
 setPermissionModal(false);
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to add permission(s)", "error");
 }
 }; 

 const handleEditPermissionChange = (e) => { 
 const { name, value } = e.target; 
 setEditPermission((prev) => ({ ...prev, [name]: value })); 
 }; 
 
 const handleEditPermissionSubmit = async (e) => { 
 e.preventDefault(); 
 
 try { 
 const res = await api.patch(`/permissions/${editPermission._id}`, editPermission); 
 setPermissions((prev) => 
 prev.map((p) => (p._id === editPermission._id ? res.data.data : p)) 
 ); 
 setEditPermission(null); 
 setEditPermissionModal(false); 
 swal("Success", "Permission updated!", "success"); 
 } catch (err) { 
 swal("Error", err.response?.data?.message || "Failed to update permission", "error"); 
 } 
 }; 
 
 const handleDeletePermission = async (permId) => { 
 const willDelete = await swal({ 
 title: "Are you sure?", 
 text: "This permission will be permanently deleted!", 
 icon: "warning", 
 buttons: ["Cancel", "Delete"], 
 dangerMode: true, 
 }); 
 
 if (!willDelete) return; 
 
 try { 
 await api.delete(`/permissions/${permId}`); 
 setPermissions((prev) => prev.filter((p) => p._id !== permId)); 
 swal("Deleted!", "Permission deleted successfully!", "success"); 
 } catch (err) { 
 swal("Error", err.response?.data?.message || "Failed to delete permission", "error"); 
 } 
 }; 
 
 const svgMenu = ( 
 <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1"> 
 <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd"> 
 <circle fill="#000000" cx="5" cy="12" r="2"></circle> 
 <circle fill="#000000" cx="12" cy="12" r="2"></circle> 
 <circle fill="#000000" cx="19" cy="12" r="2"></circle> 
 </g> 
 </svg> 
 ); 
 
 return ( 
 <Fragment> 
 <PageTitle activeMenu="Permissions" motherMenu="Users" /> 
 <Row> 
 <Col lg={12}> 
 <Card className="shadow-sm border-0"> 
 <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center flex-wrap"> 
 <Card.Title className="text-primary fw-bold mb-0">Permissions Catalog</Card.Title> 
 <div className="d-flex align-items-center flex-wrap gap-2 mt-2 mt-sm-0"> 
 <div className="input-group input-group-sm" style={{ width: '250px' }}> 
 <span className="input-group-text bg-light border-end-0">
 <i className="fa fa-search text-muted"></i>
 </span>
 <input 
 type="text" 
 className="form-control form-control-sm bg-light border-start-0" 
 placeholder="Search permissions..." 
 value={searchQuery} 
 onChange={(e) => setSearchQuery(e.target.value)} 
 /> 
 </div> 
 {canCreatePermission && ( 
 <button 
 className="btn btn-primary shadow-sm px-3" 
 onClick={() => setPermissionModal(true)} 
 > 
 <i className="fa fa-plus me-2"></i>Add Permission 
 </button> 
 )} 
 </div> 
 </Card.Header> 
 <Card.Body className="p-0"> 
 <Table responsive hover className="mb-0 align-middle"> 
 <thead className="bg-light"> 
 <tr> 
 <th className="px-4 py-3 text-uppercase fs-12 font-w600 text-muted" style={{ width: '50px' }}>#</th> 
 <th className="py-3 text-uppercase fs-12 font-w600 text-muted">Permission String</th> 
 <th className="py-3 text-uppercase fs-12 font-w600 text-muted">Scope / Description</th> 
 {(canEditPermission || canDeletePermission) && <th className="text-end px-4 py-3 text-uppercase fs-12 font-w600 text-muted">Action</th>} 
 </tr> 
 </thead> 
 <tbody> 
 {currentPermissions.length > 0 ? ( 
 currentPermissions.map((perm, i) => ( 
 <tr key={perm._id}> 
 <td className="px-4 text-muted">{indexOfFirstItem + i + 1}</td> 
 <td> 
 <Badge bg="light" className="text-info border border-info-light px-3 py-2 fs-12"> 
 {perm.name} 
 </Badge> 
 </td> 
 <td className="text-muted fs-14">
 {perm.description || <span className="fst-italic opacity-50">No description provided</span>}
 </td> 
 {(canEditPermission || canDeletePermission) && ( 
 <td className="text-end px-4"> 
 <Dropdown className="ms-auto"> 
 <Dropdown.Toggle 
 as="div" 
 className="btn-link p-0 no-caret" 
 style={{ cursor: "pointer" }} 
 > 
 {svgMenu} 
 </Dropdown.Toggle> 
 
 <Dropdown.Menu align="end" className="border-0 shadow-sm" as={PortalMenu}> 
 {canEditPermission && (
 <Dropdown.Item 
 className="py-2"
 onClick={() => { 
 setEditPermission(perm); 
 setEditPermissionModal(true); 
 }} 
 > 
 <i className="fa fa-edit me-2 text-primary"></i> Edit 
 </Dropdown.Item> 
 )}
 {canDeletePermission && (
 <Dropdown.Item 
 className="text-danger py-2" 
 onClick={() => handleDeletePermission(perm._id)} 
 > 
 <i className="fa fa-trash me-2"></i> Delete 
 </Dropdown.Item> 
 )}
 </Dropdown.Menu> 
 </Dropdown> 
 </td> 
 )} 
 </tr> 
 )) 
 ) : ( 
 <tr> 
 <td colSpan={(canEditPermission || canDeletePermission) ? 4 : 3} className="text-center py-5"> 
 <div className="text-muted">No permissions found matching your search.</div> 
 </td> 
 </tr> 
 )} 
 </tbody> 
 </Table> 
 <AppPagination
 currentPage={currentPage}
 totalPages={totalPages}
 totalCount={filteredPermissions.length}
 itemsLength={currentPermissions.length}
 limit={itemsPerPage}
 onPageChange={paginate}
 />
 </Card.Body> 
 </Card> 
 </Col> 
 </Row> 
 
 {/* ADD PERMISSION MODAL */} 
 <Modal show={permissionModal} onHide={() => { setPermissionModal(false); setBulkRows([{ name: "", description: "" }]); }} centered className="modal-light" size="lg"> 
 <Modal.Header closeButton className="border-bottom-0"> 
 <Modal.Title className="fw-bold modal-title-custom">Add New Permission</Modal.Title> 
 </Modal.Header> 
 <Form onSubmit={handlePermissionSubmit}> 
 <Modal.Body className="py-3">
 <div className="d-flex align-items-center mb-2">
 <small className="text-muted flex-grow-1">Format: <code>action:module</code> &nbsp;(e.g., <code>edit:users</code>). Add multiple rows to create in bulk.</small>
 </div>
 {/* Header row */}
 <div className="d-flex gap-2 mb-1 px-1">
 <div style={{ flex: '0 0 36px' }}></div>
 <div className="fw-medium text-dark" style={{ flex: '1', fontSize: 13 }}>Permission Key <span className="text-danger">*</span></div>
 <div className="fw-medium text-dark" style={{ flex: '1', fontSize: 13 }}>Description</div>
 <div style={{ width: 32 }}></div>
 </div>
 {/* Rows */}
 <div className="d-flex flex-column gap-2">
 {bulkRows.map((row, index) => (
 <div key={index} className="d-flex gap-2 align-items-center">
 <div className="text-muted text-center" style={{ flex: '0 0 36px', fontSize: 13 }}>{index + 1}</div>
 <Form.Control
 type="text"
 className="bg-light border-0 py-2"
 placeholder="e.g. read:workorders"
 value={row.name}
 onChange={e => handleBulkRowChange(index, "name", e.target.value)}
 style={{ flex: 1 }}
 />
 <Form.Control
 type="text"
 className="bg-light border-0 py-2"
 placeholder="Brief description..."
 value={row.description}
 onChange={e => handleBulkRowChange(index, "description", e.target.value)}
 style={{ flex: 1 }}
 />
 <button
 type="button"
 className="btn btn-sm btn-light text-danger border-0"
 style={{ width: 32, padding: '4px 0' }}
 disabled={bulkRows.length === 1}
 onClick={() => removeBulkRow(index)}
 title="Remove row"
 >
 <i className="fa fa-times"></i>
 </button>
 </div>
 ))}
 </div>
 <button
 type="button"
 className="btn btn-sm btn-outline-primary mt-3"
 onClick={addBulkRow}
 >
 <i className="fa fa-plus me-2"></i>Add Row
 </button>
 </Modal.Body> 
 <Modal.Footer className="border-top-0 pt-0"> 
 <button className="btn btn-light shadow-none text-muted" type="button" onClick={() => { setPermissionModal(false); setBulkRows([{ name: "", description: "" }]); }}>Cancel</button> 
 <button className="btn btn-primary px-4 shadow-sm" type="submit">
 {bulkRows.filter(r => r.name.trim()).length > 1
 ? `Create ${bulkRows.filter(r => r.name.trim()).length} Permissions`
 : "Create Permission"}
 </button> 
 </Modal.Footer> 
 </Form> 
 </Modal> 
 
 {/* EDIT PERMISSION MODAL */} 
 <Modal show={editPermissionModal} onHide={() => setEditPermissionModal(false)} centered className="modal-light"> 
 <Modal.Header closeButton className="border-bottom-0"> 
 <Modal.Title className="fw-bold modal-title-custom">Edit Permission</Modal.Title> 
 </Modal.Header> 
 <Form onSubmit={handleEditPermissionSubmit}> 
 <Modal.Body className="py-4"> 
 {editPermission && (
 <Fragment>
 <Form.Group className="mb-4"> 
 <Form.Label className="text-dark fw-medium">Permission Key</Form.Label> 
 <Form.Control 
 type="text" 
 name="name" 
 value={editPermission.name} 
 onChange={handleEditPermissionChange} 
 required 
 className="bg-light border-0 py-2"
 /> 
 </Form.Group> 
 <Form.Group className="mb-0"> 
 <Form.Label className="text-dark fw-medium">Description</Form.Label> 
 <Form.Control 
 as="textarea" 
 name="description" 
 rows={3}
 value={editPermission.description} 
 onChange={handleEditPermissionChange} 
 className="bg-light border-0"
 /> 
 </Form.Group> 
 </Fragment>
 )}
 </Modal.Body> 
 <Modal.Footer className="border-top-0 pt-0"> 
 <button className="btn btn-light shadow-none text-muted" type="button" onClick={() => setEditPermissionModal(false)}>Cancel</button> 
 <button className="btn btn-primary px-4 shadow-sm" type="submit">Update Permission</button> 
 </Modal.Footer> 
 </Form> 
 </Modal> 
 <style>{`
 .fs-12 { font-size: 11.5px; }
 .fs-14 { font-size: 14px; }
 .border-info-light { border-color: rgba(4, 190, 254, 0.1) !important; }
 .badge-lg { padding: 0.6em 1em; }
 .text-dark { color: #2d3436 !important; }
 .text-muted { color: #7f8c8d !important; }
 .bg-light { background-color: #f8faff !important; }
 .modal-content { border: none; border-radius: 12px; }
 .modal-header { background-color: #fff; border-bottom: 1px solid #f1f2f6; }
                .modal-title-custom { color: var(--primary, #0078D7); }
                [data-theme-version="dark"] .modal-header { background-color: #1e1e2d !important; border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important; }
                [data-theme-version="dark"] .modal-content { background-color: #1e1e2d !important; color: #f1f5f9 !important; }
                [data-theme-version="dark"] .modal-title, [data-theme-version="dark"] .modal-title-custom { color: #ffffff !important; }
                [data-theme-version="dark"] .modal-header .btn-close { filter: invert(1) grayscale(100%) brightness(200%) !important; }
                [data-theme-version="dark"] .text-dark { color: #f1f5f9 !important; }
                [data-theme-version="dark"] .modal-body .form-control { background-color: #161717 !important; border: 1px solid #2b2b2b !important; color: #ffffff !important; }
                [data-theme-version="dark"] .modal-footer .btn-light { background-color: rgba(255, 255, 255, 0.08) !important; color: #cbd5e1 !important; border: 1px solid rgba(255, 255, 255, 0.15) !important; }
 .card { border-radius: 12px; }
 .table thead th { background-color: #f8faff; border-bottom: 1px solid #f1f2f6; }
 .custom-pagination .page-link { color: #886cff; border-radius: 4px; margin: 0 2px; border: none; background: #f8faff; font-size: 13px; }
 .custom-pagination .page-item.active .page-link { background-color: #886cff; color: #fff; }
 .custom-pagination .page-item.disabled .page-link { background: transparent; color: #ccc; }
 `}</style>
 </Fragment> 
 ); 
 }; 
 
 export default Permissions; 
